/**
 * melband-vocal-separator.js — page-side facade of «Максимум», the third engine of /vocal-remover: Kim's
 * Mel-Band RoFormer (vocals) on this device's GPU. The model is the one the paid
 * server lane runs for /stem-splitter's vocals (Kim MelBand, MIT), in the ONNX
 * export for the GPU by audiojs (MIT): the weights by Kimberley Jensen, the
 * architecture by Wang et al. (ByteDance), the code by Phil Wang and Roman
 * Solovyev (ZFTurbo). A page that runs it in front of a user shows that credit
 * (the `max_credit` key).
 *
 * WHO MAY RUN IT is decided here, BEFORE a byte of the 464 MB moves (support()):
 * a weak device must not even start. Every «no» has a name, and the order is
 * cheapest first:
 *   not_wired · no_worker · webkit · mobile · no_webgpu · low_memory · network ·
 *   unfit_here · pace_unknown · slow · software_gpu · gpu_limits · gpu_rejected
 *   (everything before software_gpu is read from memory — no request, no script:
 *   most visitors never measured a pace and stop at pace_unknown for free)
 *   • mobile — a phone runs Kim (67 MB) into failure 36 % of the time (15.10);
 *     seven times the model is not a phone's;
 *   • low_memory / network — window.__deviceCap.modelAffordable, the one owner of
 *     «can this device afford a model now»: the session holds ~0.93 GB of float32
 *     weights on the GPU and as much again in wasm while it is built (8 GB at least),
 *     and 464 MB is not a download for Save-Data or a slow connection;
 *   • gpu_rejected / unfit_here — this GPU already answered the known question
 *     wrongly (OrtEpVerify, 30 days), or the model already failed here for a
 *     reason a retry will not change: out of memory, a lost device (30 days);
 *   • pace_unknown / slow — the SPEED gate. Its witness is this device's own
 *     measured pace, never a guess from its class: the pace of a previous run of
 *     this engine, or else the pace Kim («Ультра») measured here, times KIM_RATIO. No
 *     measured pace — no offer: the page offers «Максимум» after an «Ультра»
 *     result, which is exactly when the pace has just been measured.
 *
 * Public API:
 *   support()                    → Promise<{ok, reason, pace?, adapter?}>   cheap, downloads nothing
 *   pace()                       → {rate, source:'own'|'kim'} | null          seconds of work per second of audio
 *   estimateSec(durationSec)     → seconds of work for a track (no download), or null
 *   modelCached()                → Promise<bool>     will a split download nothing?
 *   modelMB                      → the download, MB (from the page's <meta>)
 *   split(audioBuffer, {onProgress(frac, stage, info), onBeat, signal, slug})
 *       → Promise<{vocal, instrumental}>  AudioBuffers in the caller's rate and length;
 *         instrumental = the track less the vocals (exactly, sample by sample)
 *   release()                    → terminates the worker (the only way its memory is given back)
 *
 * Wiring comes from the page: the worker URL (versioned, deps in `?d=`), the model
 * URL pair and its size are <meta> tags rendered by tools/partials/melband-engine;
 * the ORT build is window.__ortBuild.nativeGpu().
 */
(function () {
'use strict';

if (window.MelbandVocalSeparator) return;

var SR = 44100;
var MIN_DEVICE_GB = 8;
/* Seconds of Mel-Band RoFormer work per second of Kim work on the same device
   (2026-10-28, RTX 4060 Laptop): ×4.5 for the whole pipelines, ×6.2 for the two networks
   alone. Kim's stored pace is its WHOLE pipeline (its first run on a device even carries the
   shader compilation), so the GATE projects it by the pipelines' ratio — by the networks'
   one it turned that very laptop away while it runs a song in 0.57 of its length (stand,
   28.10). The PRICE the person reads is projected by ×6.2, where a weaker GPU tends (its
   network takes a larger share of the time): better to quote more minutes than it takes.
   After the first run of this engine both read the device's own pace.
   docs/why/vocal-remover-max-engine.md */
var KIM_RATIO = 4.5;
var PRICE_RATIO = 6.2;
/* The ceiling of the speed gate: projected seconds of work per second of audio.
   1.0 — a song takes about as long as it plays. */
var RATE_MAX = 1.0;
var RATE_KEY = 'tmb_melband_rate';            // this device's own measured pace
var UNFIT_KEY = 'tmb_melband_unfit';          // {ts, why} — a failure a retry will not change
var UNFIT_TTL = 30 * 24 * 3600 * 1000;

function meta(name) { var m = document.querySelector('meta[name="' + name + '"]'); return (m && m.content) || ''; }
function abs(u) { try { return new URL(u, window.location.href).href; } catch (_) { return u; } }
function tele(slug, action, payload) {
    try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: slug || 'melband', action: action }, payload || {})); } catch (_) {}
}
function typed(code, message) { var e = new Error(message || code); e.code = code; return e; }
function nowActive() { return typeof window.__activeNow === 'function' ? window.__activeNow() : performance.now(); }
function lsGet(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
function lsDel(k) { try { localStorage.removeItem(k); } catch (_) {} }

/* Silence watchdog on VISIBLE time (a hidden tab loads and runs nothing on time — CLAUDE.md
   «Дедлайн … сторож «воркер молчит N с»»): a worker that stops talking is a GPU that stopped
   answering. Every message re-arms it — a beat after every run of the model, a progress tick. */
var INIT_SILENCE_MS = 180000;            // the session of 464 MB and the known-answer runs
var SEG_SILENCE_MIN_MS = 60000;          // one segment: max(this, 10 × the known-answer segment)
function visibleTimeout(fn, ms) {
    if (typeof window.__visibleTimeout === 'function') return window.__visibleTimeout(fn, ms);
    var h = setTimeout(fn, ms); return function () { clearTimeout(h); };
}
function watchdog(ms, onSilence) {
    var cancel = null, dead = false;
    var arm = function () { if (dead) return; if (cancel) cancel(); cancel = visibleTimeout(function () { dead = true; onSilence(); }, ms); };
    arm();
    return { poke: arm, stop: function () { dead = true; if (cancel) cancel(); } };
}

function loadScript(path, probe) {
    if (probe()) return Promise.resolve();
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = window.__jsSrc ? window.__jsSrc(path) : path;
        s.onload = function () { probe() ? resolve() : reject(typed('dep_missing', path + ' loaded without its global')); };
        s.onerror = function () { reject(typed('dep_missing', path + ' failed to load')); };
        document.head.appendChild(s);
    });
}
function needVerifier() { return loadScript('/js/ort-ep-verify.js', function () { return !!window.OrtEpVerify; }); }
function needDownloader() { return loadScript('/js/model-download.js', function () { return !!self.__mdFetchBytes; }); }

function isPhone() {
    try { if (navigator.userAgentData && navigator.userAgentData.mobile) return true; } catch (_) {}
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}
/* «Can this device afford the model now» has ONE owner (CLAUDE.md, __deviceCap.modelAffordable):
   memory always (through the page's memory budget), the connection and Save-Data only while the
   model is not on disk yet. */
function affordable() {
    var DC = window.__deviceCap;
    if (!DC || typeof DC.modelAffordable !== 'function') return { ok: true };
    return DC.modelAffordable({ mb: +(meta('melband-model-mb') || 0) || 464, minMemoryGB: MIN_DEVICE_GB, url: meta('melband-model-url'), ml: { requiresWebGPU: true } });
}

function unfitHere() {
    var raw = lsGet(UNFIT_KEY);
    if (!raw) return null;
    try {
        var o = JSON.parse(raw);
        if (o && o.ts && Date.now() - o.ts < UNFIT_TTL) return o;
    } catch (_) {}
    lsDel(UNFIT_KEY);
    return null;
}
/* Failures a retry will not change on this device: memory, a lost GPU. A throw that
   is a bad day (a cut download, a cancel) is not one of them. */
var UNFIT_CODES = { gpu_out_of_memory: 1, gpu_lost: 1, ep_ladder_exhausted: 1, gpu_output_corrupt: 1, gpu_stalled: 1 };
function classify(e) {
    var m = String((e && e.message) || e || '');
    if (e && e.code && UNFIT_CODES[e.code]) return e.code;
    if (/out of memory|OOM|bad_alloc|Failed to allocate|mappedAtCreation|createBuffer/i.test(m)) return 'gpu_out_of_memory';
    if (/device (was )?lost|GPUDeviceLostInfo|device is lost/i.test(m)) return 'gpu_lost';
    return null;
}

function pace() {
    var own = parseFloat(lsGet(RATE_KEY));
    if (isFinite(own) && own > 0) return { rate: own, source: 'own' };
    var M = window.MdxStemSeparator;
    var kim = (M && typeof M.storedRate === 'function') ? M.storedRate() : null;
    if (kim && isFinite(kim) && kim > 0) return { rate: +(kim * KIM_RATIO).toFixed(3), source: 'kim', kim: kim };
    return null;
}
function estimateSec(durationSec) {
    var p = pace();
    if (!p || !(durationSec > 0)) return null;
    var rate = p.source === 'kim' ? p.kim * PRICE_RATIO : p.rate;
    // + the known-answer test before and after (three segments ≈ 12 s of audio) and the session
    return Math.round(durationSec * rate + 12 * rate + 6);
}

var adapterInfo = null, cacheKey = null;
function support() {
    if (!window.Worker) return Promise.resolve({ ok: false, reason: 'no_worker' });
    if (!meta('melband-worker-url') || !meta('melband-model-url')) return Promise.resolve({ ok: false, reason: 'not_wired' });
    if (!window.__ortBuild || typeof window.__ortBuild.nativeGpu !== 'function') return Promise.resolve({ ok: false, reason: 'not_wired' });
    if (!window.__ortBuild.nativeGpu()) return Promise.resolve({ ok: false, reason: 'webkit' });
    if (isPhone()) return Promise.resolve({ ok: false, reason: 'mobile' });
    if (!navigator.gpu) return Promise.resolve({ ok: false, reason: 'no_webgpu' });
    var aff = affordable();
    if (!aff.ok) return Promise.resolve({ ok: false, reason: aff.soft ? 'network' : 'low_memory', why: aff.reason || null });
    var un = unfitHere();
    if (un) return Promise.resolve({ ok: false, reason: 'unfit_here', why: un.why || null });
    var p = pace();
    if (!p) return Promise.resolve({ ok: false, reason: 'pace_unknown' });
    if (p.rate > RATE_MAX) return Promise.resolve({ ok: false, reason: 'slow', pace: p });
    return needVerifier().then(function () {
        return window.OrtEpVerify.adapterIdentity();
    }).then(function (id) {
        adapterInfo = id || null;
        if (!id || !id.present) return { ok: false, reason: 'no_webgpu' };
        if (id.fallback) return { ok: false, reason: 'software_gpu' };
        var ua = (navigator.userAgent.match(/(?:Chrome|Firefox|Edg)\/(\d+)/) || [])[1] || '0';
        cacheKey = window.OrtEpVerify.verdictKey(['melband', 'ort-native', id.vendor, id.architecture, 'b' + ua]);
        if (window.OrtEpVerify.isRejected(cacheKey, 'webgpu')) return { ok: false, reason: 'gpu_rejected' };
        return gpuLimitsOk().then(function (lim) {
            if (!lim) return { ok: false, reason: 'gpu_limits' };
            return { ok: true, pace: p, adapter: id };
        });
    }, function () { return { ok: false, reason: 'no_webgpu' }; });
}
/* The graph keeps every tensor under 100 MB, but it needs the default 128 MiB
   storage binding and buffers of that size — an adapter that cannot even grant
   those cannot hold this model. */
var _limits = null;
function gpuLimitsOk() {
    if (_limits) return _limits;
    _limits = navigator.gpu.requestAdapter({ powerPreference: 'high-performance' }).then(function (a) {
        if (!a || !a.limits) return false;
        return a.limits.maxStorageBufferBindingSize >= 134217728 && a.limits.maxBufferSize >= 268435456;
    }).catch(function () { return false; });
    return _limits;
}

// ── Worker life-cycle ──
var worker = null, ready = null, readyInfo = null, pending = {}, seq = 0, currentSlug = null;
var beatHooks = [], loadHooks = [];

function release() {
    if (worker) { try { worker.terminate(); } catch (_) {} }
    worker = null; ready = null; readyInfo = null;
    Object.keys(pending).forEach(function (id) { try { pending[id].reject(typed('released', 'separator released')); } catch (_) {} });
    pending = {};
}

function onVerdict(v) {
    var flat = window.OrtEpVerify ? window.OrtEpVerify.telemetryOf(v, adapterInfo) : { ep: v && v.provider, ok: v && v.ok ? 1 : 0 };
    flat.why = v && v.why || null; flat.worst_db = v && v.worstDb; flat.seg_ms = v && v.segMs || null;
    if (v && v.bands) flat.bands = v.bands;
    tele(currentSlug, 'melband_verdict', flat);
    if (v && !v.ok && !v.error && !v.skipped && v.provider === 'webgpu' && cacheKey && window.OrtEpVerify) {
        try { window.OrtEpVerify.remember(cacheKey, 'webgpu', { kind: 'kat', why: v.why || null }); } catch (_) {}
    }
}

function fetchModel(opts) {
    return needDownloader().then(function () {
        var urls = [meta('melband-model-url'), meta('melband-model-url-fallback')].filter(Boolean);
        var tryUrl = function (i) {
            return self.__mdFetchBytes(urls[i], {
                signal: opts.signal,
                onProgress: function (got, total) {
                    if (!(total > 0)) return;
                    var f = Math.min(1, got / total);
                    if (opts.onProgress) opts.onProgress(f, got, total);
                    loadHooks.slice().forEach(function (h) { try { h(f, got, total); } catch (_) {} });
                }
            }).catch(function (e) {
                if (i + 1 < urls.length && !(opts.signal && opts.signal.aborted)) {
                    tele(currentSlug, 'melband_model_fallback', { reason: String(e && e.message || e).slice(0, 80) });
                    return tryUrl(i + 1);
                }
                throw e;
            });
        };
        return tryUrl(0);
    });
}

function preload(slug, opts) {
    opts = opts || {};
    currentSlug = slug || currentSlug;
    if (ready) return ready;
    var t0 = performance.now();
    ready = support().then(function (sup) {
        if (!sup.ok) throw typed('melband_unsupported_' + sup.reason, 'Studio cannot run here: ' + sup.reason);
        return fetchModel(opts);
    }).then(function (bytes) {
        var dlMs = Math.round(performance.now() - t0);
        var buf = bytes.buffer.byteLength === bytes.length ? bytes.buffer : bytes.slice().buffer;
        bytes = null;
        var build = window.__ortBuild.nativeGpu();
        return new Promise(function (resolve, reject) {
            try { worker = new Worker(meta('melband-worker-url')); }
            catch (e) { reject(typed('worker_failed', String(e && e.message || e))); return; }
            var initWatch = watchdog(INIT_SILENCE_MS, function () {
                reject(typed('gpu_stalled', 'model session silent for ' + INIT_SILENCE_MS / 1000 + ' s'));
            });
            worker.onerror = function (ev) {
                initWatch.stop();
                var err = typed('worker_failed', (ev && ev.message) || 'worker error');
                reject(err);
                Object.keys(pending).forEach(function (id) { pending[id].reject(err); });
                pending = {};
            };
            worker.onmessage = function (ev) {
                var m = ev.data || {};
                if (!readyInfo) initWatch.poke();
                if (m.type === 'verdict') onVerdict(m.verdict);
                else if (m.type === 'ready') { initWatch.stop(); readyInfo = { provider: m.provider, segMs: m.segMs, dlMs: dlMs }; resolve(readyInfo); }
                else if (m.type === 'progress') { var p = pending[m.id]; if (p && p.onProgress) p.onProgress(m.done, m.of); }
                else if (m.type === 'beat') beatHooks.slice().forEach(function (f) { try { f(m.id); } catch (_) {} });
                else if (m.type === 'result') { var r = pending[m.id]; if (r) { delete pending[m.id]; r.resolve(m); } }
                else if (m.type === 'error') {
                    var err = typed(m.code || 'melband_error', m.message);
                    err.verdicts = m.verdicts || null;
                    if (m.id != null && pending[m.id]) { var q = pending[m.id]; delete pending[m.id]; q.reject(err); }
                    else { initWatch.stop(); reject(err); }
                }
            };
            worker.postMessage({
                type: 'init', modelBytes: buf,
                ortUrl: abs(build.url), wasmBase: abs(build.wasmBase),
                ortFallbackUrl: abs(build.fallbackUrl), fallbackWasmBase: abs(build.fallbackWasmBase)
            }, [buf]);
        });
    }).then(function (info) {
        tele(slug, 'melband_preload_ok', { ms: Math.round(performance.now() - t0), dl_ms: info.dlMs, ep: info.provider, seg_ms: info.segMs || 0 });
        return info;
    }).catch(function (e) {
        var why = classify(e);
        if (why) lsSet(UNFIT_KEY, JSON.stringify({ ts: Date.now(), why: why }));
        tele(slug, 'melband_preload_fail', { code: e && e.code || null, unfit: why, msg: String(e && e.message || e).slice(0, 80) });
        release();
        throw e;
    });
    return ready;
}

// ── Audio in / out: the model's 44.1 kHz is ITS passport, not the caller's format ──
function renderAt(buffer, rate, length) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var ctx = new Ctor(2, length, rate);
    var node = ctx.createBufferSource();
    node.buffer = buffer; node.connect(ctx.destination); node.start();
    return ctx.startRendering();
}
function toStereo44k(audioBuffer) {
    var p = audioBuffer.sampleRate === SR ? Promise.resolve(audioBuffer)
        : renderAt(audioBuffer, SR, Math.max(1, Math.round(audioBuffer.duration * SR)));
    return p.then(function (b) {
        return [new Float32Array(b.getChannelData(0)), new Float32Array(b.getChannelData(b.numberOfChannels > 1 ? 1 : 0))];
    });
}
function toCallerBuffer(pair, audioBuffer) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var n = pair[0].length;
    var src = new Ctor(2, n, SR).createBuffer(2, n, SR);
    src.getChannelData(0).set(pair[0]); src.getChannelData(1).set(pair[1]);
    if (audioBuffer.sampleRate === SR && audioBuffer.length === n) return Promise.resolve(src);
    // linear resampling keeps a sum a sum (tests/playwright/vocal-remover-stem-rate-contract.mjs)
    return renderAt(src, audioBuffer.sampleRate, audioBuffer.length);
}
/* The track less the vocals, in the CALLER's buffer geometry: exactly the mix minus
   the vocal stem, sample by sample, as the page's strength slider assumes. */
function remainder(audioBuffer, vocal) {
    var Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    var nc = audioBuffer.numberOfChannels, n = audioBuffer.length;
    var out = new Ctor(nc, n, audioBuffer.sampleRate).createBuffer(nc, n, audioBuffer.sampleRate);
    for (var c = 0; c < nc; c++) {
        var mix = audioBuffer.getChannelData(c), v = vocal.getChannelData(Math.min(c, vocal.numberOfChannels - 1)), d = out.getChannelData(c);
        for (var i = 0; i < n; i++) d[i] = mix[i] - v[i];
    }
    return out;
}

/* A real AbortSignal for whatever the caller handed in. /vocal-remover cancels by a
   flag ({get aborted()}) that has no event and that fetch() refuses outright (the
   downloader passes the signal to fetch); it is watched here and turned into a
   controller the downloader and the worker both obey. */
function realSignal(sig) {
    if (!sig) return { signal: null, done: function () {} };
    if (typeof AbortSignal !== 'undefined' && sig instanceof AbortSignal) return { signal: sig, done: function () {} };
    var ac = new AbortController();
    if (sig.aborted) { ac.abort(); return { signal: ac.signal, done: function () {} }; }
    var iv = setInterval(function () { if (sig.aborted) { clearInterval(iv); ac.abort(); } }, 200);
    return { signal: ac.signal, done: function () { clearInterval(iv); } };
}

/* A cancel takes effect NOW: the promise rejects and the worker is terminated —
   which also hands back the model's memory, of no more use to a cancelled run. */
function abortable(promise, signal) {
    if (!signal) return promise;
    return new Promise(function (resolve, reject) {
        var stop = function () { reject(new DOMException('aborted', 'AbortError')); release(); };
        if (signal.aborted) { stop(); return; }
        signal.addEventListener('abort', stop, { once: true });
        promise.then(function (v) { signal.removeEventListener('abort', stop); resolve(v); },
                     function (e) { signal.removeEventListener('abort', stop); reject(e); });
    });
}

function split(audioBuffer, opts) {
    opts = opts || {};
    var slug = opts.slug, onProgress = opts.onProgress || null;
    var rs = realSignal(opts.signal), signal = rs.signal;
    var onBeat = typeof opts.onBeat === 'function' ? opts.onBeat : null;
    if (onBeat) beatHooks.push(onBeat);
    var onLoad = onProgress ? function (f, got, total) { onProgress(f * 0.25, 'download', { phase: 'download', frac: f, got: got, total: total }); } : null;
    if (onLoad) loadHooks.push(onLoad);
    var unhook = function () {
        var i = beatHooks.indexOf(onBeat); if (i >= 0) beatHooks.splice(i, 1);
        var j = loadHooks.indexOf(onLoad); if (j >= 0) loadHooks.splice(j, 1);
        rs.done();
    };
    var tWork = null;
    return abortable(preload(slug, { signal: signal }).then(function () {
        if (onLoad) { var j = loadHooks.indexOf(onLoad); if (j >= 0) loadHooks.splice(j, 1); }
        tWork = nowActive();
        return toStereo44k(audioBuffer);
    }).then(function (chans) {
        if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
        var id = ++seq;
        var budget = Math.max(SEG_SILENCE_MIN_MS, 10 * ((readyInfo && readyInfo.segMs) || 3000));
        return new Promise(function (resolve, reject) {
            var watch = watchdog(budget, function () {
                if (!pending[id]) return;
                delete pending[id];
                reject(typed('gpu_stalled', 'a segment took longer than ' + Math.round(budget / 1000) + ' s'));
            });
            var beat = function (bid) { if (bid === id) watch.poke(); };
            beatHooks.push(beat);
            var done = function () { watch.stop(); var i = beatHooks.indexOf(beat); if (i >= 0) beatHooks.splice(i, 1); };
            pending[id] = {
                resolve: function (v) { done(); resolve(v); }, reject: function (e) { done(); reject(e); },
                onProgress: function (d, of) { watch.poke(); if (onProgress) onProgress(0.25 + 0.75 * d / of, 'separate', { phase: 'compute', done: d, of: of, frac: d / of }); }
            };
            worker.postMessage({ type: 'split', id: id, L: chans[0], R: chans[1] }, [chans[0].buffer, chans[1].buffer]);
        });
    }), signal).then(function (m) {
        return toCallerBuffer(m.vocal, audioBuffer).then(function (vocal) {
            var workSec = (nowActive() - tWork) / 1000;
            var rate = workSec / Math.max(1, audioBuffer.duration);
            // this device's own pace from now on: the gate and the price read it first
            lsSet(RATE_KEY, String(+rate.toFixed(3)));
            tele(slug, 'melband_split_ok', { sec: Math.round(audioBuffer.duration), ms: Math.round(workSec * 1000), rate: +rate.toFixed(3), segments: m.segments, seg_ms: m.segMs });
            unhook();
            return { vocal: vocal, instrumental: remainder(audioBuffer, vocal) };
        });
    }).catch(function (e) {
        unhook();
        if ((signal && signal.aborted) || (e && (e.name === 'AbortError' || e.code === 'cancelled' || e.code === 'released'))) throw new DOMException('aborted', 'AbortError');
        var why = classify(e);
        if (why) lsSet(UNFIT_KEY, JSON.stringify({ ts: Date.now(), why: why }));
        tele(slug, 'melband_split_fail', { code: e && e.code || null, unfit: why, msg: String(e && e.message || e).slice(0, 80) });
        release();
        throw e;
    });
}

/* Is the model already in this browser (any cache, by file name)? */
function modelCached() {
    if (typeof caches === 'undefined' || !caches.keys) return Promise.resolve(false);
    var file = (meta('melband-model-url').split('?')[0].split('/').pop()) || '';
    if (!file) return Promise.resolve(false);
    return caches.keys().then(function (names) {
        return Promise.all(names.map(function (n) {
            return caches.open(n).then(function (c) { return c.keys(); }).catch(function () { return []; });
        }));
    }).then(function (lists) {
        for (var i = 0; i < lists.length; i++) for (var j = 0; j < lists[i].length; j++) {
            var u = String((lists[i][j] && lists[i][j].url) || '').split('?')[0];
            if (u.slice(-file.length - 1) === '/' + file) return true;
        }
        return false;
    }).catch(function () { return false; });
}

window.MelbandVocalSeparator = {
    support: support,
    pace: pace,
    estimateSec: estimateSec,
    modelCached: modelCached,
    preload: preload,
    split: split,
    release: release,
    get modelMB() { return +(meta('melband-model-mb') || 0) || null; },
    KIM_RATIO: KIM_RATIO,
    PRICE_RATIO: PRICE_RATIO,
    RATE_MAX: RATE_MAX,
    isWarm: function () { return !!readyInfo; }
};
})();
